import { useEffect, useState } from 'react'
import { useServiceContext } from '~/root'
import { RAValidationSchema } from './WFPhaseValidation'
import { useTranslation } from 'react-i18next'
import { findWhere } from 'underscore'
import { transformFromServer, transformToServer } from '~/pages/settings/workflow-templates/DataService'
import _, { cloneDeep } from 'lodash'
import Modal from '../Modal'
import { Field, Formik, yupToFormErrors } from 'formik'
import { Button, Form, FormGroup, FormCheck } from 'react-bootstrap'
import SelectUserGroup from '../SelectUserGroup'
import { RA_ACTION_TYPE } from '~/types/workflow'
import { FsiType } from '~/constants/fsi'
import { getFileMetadata, getFileTrainees, updateFileTraineesV2 } from '~/services/api/files'

import { documentTypesApiService } from '~/services/api/api.documenttypes'
import { folderTypesApiService } from '~/services/api/api.foldertypes'
import { requestDocumentChange } from '~/services/api/workflows'
import LoadingSpinner from '../LoadingSpinner'

interface RandAPhaseModalProps {
  fsi?: {
    id?: string | number
    type?: string
    name?: string
  }
  onClose?: () => void
}

interface FormDataType {
  [key: string]: any
}

interface WorkflowTemplateType {
  id?: string | number
  name?: string
}

interface TraineesType {
  usersGroups?: any
  createRAActionType?: string
}

interface ValuesType {
  readOnly?: boolean
  newMembers?: string[]
  workflowTemplate?: WorkflowTemplateType
  trainees?: TraineesType
  assignNewUsersNow?: boolean
  saveRecursivelyVar?: boolean
}

function RandAPhaseModal({ fsi, onClose }: RandAPhaseModalProps) {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { constant } = context
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [formData, setFormData] = useState<FormDataType>({})
  const [orignalFormData, setOrignalFormData] = useState<FormDataType>({})
  const [docTypeEffectiveTimeDays, setDocTypeEffectiveTimeDays] = useState<number | null>(null)
  const rAValidationSchema = RAValidationSchema()

  useEffect(() => {
    if (fsi?.id) {
      setIsLoading(true)
      let typesApi: typeof documentTypesApiService | typeof folderTypesApiService = documentTypesApiService
      if (fsi.type === context.constant.file.type.FOLDER) {
        typesApi = folderTypesApiService
      }

      // Wait for all API calls to complete
      Promise.all([
        typesApi.getAll().then(function (data: { elements?: any[] }) {
          const availableTypes = data?.elements ?? []
          return getFileMetadata(fsi.id as number)
            .then((metaData: any) => {
              const docType = findWhere(availableTypes, {
                id: metaData?.documentTypeTemplateId ?? metaData?.folderTypeTemplateId
              }) as { effectiveTimeDays?: number } | undefined
              docType ? setDocTypeEffectiveTimeDays(docType?.effectiveTimeDays ?? null) : null
            })
            .catch((err: any) => {
              console.log(`Error fetching file metadata: ${err}`)
            })
        }),
        getFileTrainees(fsi.id as number)
          .then(function (trainees: any) {
            const cloneTransformedVals = transformFromServer(cloneDeep(trainees), undefined)
            setFormData(cloneTransformedVals)
            setOrignalFormData(cloneTransformedVals)
          })
          .catch((err: any) => {
            console.log(`Error fetching file trainees: ${err}`)
          })
      ]).finally(() => {
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [fsi, context])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function dismiss(values: ValuesType, resetForm: (nextValues: any) => void) {
    if (isSaveBtnDisplayed(values)) {
      resetForm(orignalFormData)
    } else {
      doClose()
    }
  }

  function isSaveBtnDisplayed(values: ValuesType): boolean {
    return Boolean(
      (!values?.readOnly && !_.isEqual(values, orignalFormData)) || (values?.readOnly && values?.newMembers?.length)
    )
  }

  function doSave(values: ValuesType) {
    if (!fsi?.id) return

    setIsSaving(true)
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = cloneVals.saveRecursivelyVar
    delete cloneVals.saveRecursivelyVar
    if (cloneVals?.readOnly) {
      const trainees: Array<{ group?: { id: string }; user?: { id: string } }> = []
      _.forEach(cloneVals?.newMembers, (stepItem: string) => {
        const stepItemArr = stepItem.split('-')
        trainees.push(
          stepItemArr[0] === 'GROUP'
            ? {
                group: { id: stepItemArr[1] }
              }
            : {
                user: { id: stepItemArr[1] }
              }
        )
      })
      const request = {
        workflowType: constant.workflow.type.TRAINING,
        fileIds: [fsi.id],
        trainees: trainees
      }
      requestDocumentChange(request).then(
        function () {
          setIsSaving(false)
          doClose()
        },
        function (data: any) {
          setIsSaving(false)
          context.service.toast.error(
            data.data.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR')
          )
        }
      )
    } else {
      const finalData = transformToServer(cloneVals)
      updateFileTraineesV2(fsi.id as number, finalData, false, saveRecursivelyVar).then(
        (_data: any) => {
          setIsSaving(false)
          doClose()
        },
        (data: any) => {
          setIsSaving(false)
          context.service.toast.error(
            data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR')
          )
        }
      )
    }
  }

  return (
    <Modal show={isOpen} title={translate('BROWSER_FILE_SETTINGS_TRAINEES_H_TITLE')} handleClose={doClose}>
      {isLoading ? (
        <Modal.Body>
          <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner centered size={40} />
          </div>
        </Modal.Body>
      ) : (
        <Formik
          innerRef={(el: any) => {
            el ? (context.store.formRef = el) : null
          }}
          initialValues={{
            ...formData
          }}
          enableReinitialize
          validate={(values: ValuesType) => {
            return rAValidationSchema
              .validate(values, {
                abortEarly: false,
                context: values
              })
              .then(
                () => {
                  return {}
                },
                (err: any) => {
                  return yupToFormErrors(err)
                }
              )
          }}
          onSubmit={(values: ValuesType) => {
            doSave(values)
          }}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            resetForm
          }: {
            values: ValuesType
            handleSubmit: () => void
            setFieldValue: (field: string, value: any) => void
            resetForm: (nextValues?: any) => void
          }) => (
            <>
              <Modal.Body style={{ position: 'relative' }}>
                {isSaving && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(255,255,255,0.8)',
                      zIndex: 1000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <LoadingSpinner centered size={40} />
                  </div>
                )}
                <div className="form-group md-form-group w100p">
                  <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                  {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
                </div>
                {values?.workflowTemplate?.id && (
                  <div className="form-group md-form-group w100p">
                    <label>{translate('WF_TEMPLATE_LABEL')}</label>
                    <p>{values.workflowTemplate?.name || '-'}</p>
                  </div>
                )}
                {!!values?.readOnly && (
                  <FormGroup>
                    <Form.Label>{translate('USERS_GROUPS')}</Form.Label>
                    <SelectUserGroup
                      isMulti={true}
                      value={values?.trainees?.usersGroups}
                      onChange={(option) => {
                        setFieldValue('trainees.usersGroups', option ? option : [])
                      }}
                      name="trainees.usersGroups"
                      isDisabled={values?.readOnly}
                    />
                  </FormGroup>
                )}
                {fsi?.type === constant.file.type.DOCUMENT && (
                  <div className="form-group md-form-group w100p mt20">
                    <label>
                      {`${translate('BROWSER_SETTINGS_TRAINING_DURATION_LABEL')}`}:{' '}
                      {`${docTypeEffectiveTimeDays != null ? docTypeEffectiveTimeDays : translate('-')}`}
                    </label>
                  </div>
                )}
                {!values?.readOnly && (
                  <>
                    <FormGroup>
                      <Form.Label>{translate('USERS_GROUPS')}</Form.Label>
                      <SelectUserGroup
                        isMulti={true}
                        value={values?.trainees?.usersGroups}
                        onChange={(option) => {
                          setFieldValue('trainees.usersGroups', option ? option : [])
                        }}
                        name="trainees.usersGroups"
                        isDisabled={values?.readOnly}
                      />
                    </FormGroup>
                    <FormGroup className="w100p">
                      <Form.Label>{translate('BROWSER_SETTINGS_TRAINING_SAVING_TYPE_HEADLINE')}</Form.Label>
                      <Field name="trainees.createRAActionType">
                        {({ field }: any) => (
                          <>
                            <FormCheck
                              {...field}
                              type="radio"
                              value={RA_ACTION_TYPE.FOR_NOBODY}
                              checked={values?.trainees?.createRAActionType === RA_ACTION_TYPE.FOR_NOBODY}
                              onChange={() => setFieldValue('trainees.createRAActionType', RA_ACTION_TYPE.FOR_NOBODY)}
                              id={`0f9eddb8-de1e-48e7-9ac0-67b3kkaa8244-${RA_ACTION_TYPE.FOR_NOBODY}`}
                              label={
                                <div className="px-2 font-weight-normal">
                                  {translate('BROWSER_SETTINGS_TRAINING_SAVING_TYPE_FUTURE_LABEL')}
                                </div>
                              }
                            />
                            <FormCheck
                              {...field}
                              type="radio"
                              value={RA_ACTION_TYPE.FOR_NEW_USERS_ONLY}
                              checked={values?.trainees?.createRAActionType === RA_ACTION_TYPE.FOR_NEW_USERS_ONLY}
                              onChange={() =>
                                setFieldValue('trainees.createRAActionType', RA_ACTION_TYPE.FOR_NEW_USERS_ONLY)
                              }
                              id={`0f9eddb8-de1e-48e7-9ac0-67b3kkaa8244-${RA_ACTION_TYPE.FOR_NEW_USERS_ONLY}`}
                              label={
                                <div className="px-2 font-weight-normal">
                                  {translate('BROWSER_SETTINGS_TRAINING_SAVING_TYPE_NEW_NOW_LABEL')}
                                </div>
                              }
                            />
                            <FormCheck
                              {...field}
                              type="radio"
                              value={RA_ACTION_TYPE.FOR_ALL_USERS}
                              checked={values?.trainees?.createRAActionType === RA_ACTION_TYPE.FOR_ALL_USERS}
                              onChange={() =>
                                setFieldValue('trainees.createRAActionType', RA_ACTION_TYPE.FOR_ALL_USERS)
                              }
                              id={`0f9eddb8-de1e-48e7-9ac0-67b3kkaa8244-${RA_ACTION_TYPE.FOR_ALL_USERS}`}
                              label={
                                <div className="px-2 font-weight-normal">
                                  {translate('BROWSER_SETTINGS_TRAINING_SAVING_TYPE_ALL_NOW_LABEL')}
                                </div>
                              }
                            />
                          </>
                        )}
                      </Field>
                    </FormGroup>
                  </>
                )}
                {!!values?.readOnly && (
                  <>
                    <FormGroup className="w100p">
                      <Field name="assignNewUsersNow">
                        {({ field }: any) => (
                          <FormCheck
                            {...field}
                            type="checkbox"
                            checked={values?.assignNewUsersNow}
                            value={true}
                            onChange={(e: any) => setFieldValue('assignNewUsersNow', e.target.checked)}
                            className="d-inline-block mr10"
                            id="7d617b01-df9d-4ae9-b611-3636c2ed1enk"
                            label={translate('BROWSER_SETTINGS_TRAINING_ASSIGN_NEW_MEMBERS_NOW')}
                          />
                        )}
                      </Field>
                    </FormGroup>
                    {values?.assignNewUsersNow && (
                      <SelectUserGroup
                        isMulti={true}
                        value={values?.newMembers}
                        onChange={(option) => {
                          setFieldValue('newMembers', option ? option : [])
                        }}
                        name="newMembers"
                      />
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                {!isSaveBtnDisplayed(values) && (
                  <Button
                    className="btn btn-primary at_btnSaveApprovers"
                    type="button"
                    onClick={() => dismiss(values, resetForm)}
                    disabled={isSaving}
                  >
                    {translate('OK')}
                  </Button>
                )}
                {!!isSaveBtnDisplayed(values) && (
                  <>
                    <Button
                      className="btn btn-close"
                      variant="link"
                      type="button"
                      onClick={() => dismiss(values, resetForm)}
                      disabled={isSaving}
                    >
                      {translate('CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      className="btn btn-primary at_btnSaveApprovers"
                      onClick={() => {
                        setFieldValue('saveRecursivelyVar', false)
                        handleSubmit()
                      }}
                      disabled={isSaving}
                    >
                      {translate('SAVE_BTN')}
                    </Button>
                  </>
                )}
                {fsi?.type === context.constant.file.type.FOLDER && isSaveBtnDisplayed(values) && (
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveAclsRecursively"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', true)
                      handleSubmit()
                    }}
                    disabled={isSaving}
                  >
                    {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                  </Button>
                )}
              </Modal.Footer>
            </>
          )}
        </Formik>
      )}
    </Modal>
  )
}

export default RandAPhaseModal
